/**
 * constants/currency.ts
 * Currency taxonomy constants and forex pair data
 *
 * Provides runtime constants for the Currency domain including:
 * - Comprehensive currency database (fiat, stablecoins, cryptocurrencies)
 * - Derived currency codes array (alphabetically sorted)
 * - Forex major and minor trading pairs
 *
 * These constants correspond to the pure type definitions in `src/types/shared/Currency.ts`.
 *
 * CURRENCY INCLUSION POLICY:
 * - Fiat: Major global currencies following ISO 4217 standards (44 currencies)
 * - Stablecoins: USD-pegged and major algorithmic stablecoins used in trading (14 currencies)
 * - Cryptocurrencies: Top 20 digital assets commonly used as quote currencies (20 currencies)
 * - Total: 78 currencies across all types
 * - Exclusions: Exotic fiat currencies, minor stablecoins, and low-volume crypto assets
 *
 * FOREX PAIR TAXONOMY:
 * - Major Pairs: G10 currency pairs with USD as base or quote (7 pairs)
 * - Minor Pairs: Cross-currency pairs between major economies excluding USD (8 pairs)
 * - Pairs are curated explicit lists, not derived/generated
 * - All pairs follow 'BASE/QUOTE' format with ISO 4217 3-letter codes
 *
 * ARCHITECTURAL CONSTRAINTS:
 * - Zero cross-domain imports (only `import type` from `types/`)
 * - No imports from `utils/` or other domain constants
 * - No side effects, pure data only
 * - All exports use `as const satisfies` for compile-time type safety
 *
 * TESTING GUIDELINES:
 * - Do NOT import from this file in tests to assert behavior
 * - Prefer importing parametric utils and passing these constants as inputs
 * - This keeps tests decoupled from constants and focused on logic
 */

// Internal absolute imports (type-only)
import type {
  CurrencyInfo
} from '@/types/shared/Currency';

/**
 * Comprehensive currency database with rich metadata
 * Includes fiat currencies, stablecoins, and major cryptocurrencies used as quote currencies
 *
 * Structure:
 * - 44 fiat currencies (AED→ZAR, alphabetically sorted)
 * - 14 stablecoins (BUSD→UST, alphabetically sorted)
 * - 20 cryptocurrencies (ALGO→NEAR, alphabetically sorted)
 *
 * Immutable via 'as const satisfies' assertion
 */
export const CURRENCY_DATABASE = {
  // Fiat Currencies (alphabetized)
  'AED': { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', type: 'fiat', keywords: ['emirates', 'dubai', 'dirham'] },
  'AUD': { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', type: 'fiat', keywords: ['australia', 'aussie', 'dollar'], minorUnit: 2 },
  'BGN': { code: 'BGN', name: 'Bulgarian Lev', symbol: 'лв', type: 'fiat', keywords: ['bulgaria', 'lev'] },
  'BHD': { code: 'BHD', name: 'Bahraini Dinar', symbol: '.د.ب', type: 'fiat', keywords: ['bahrain', 'dinar'] },
  'BRL': { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', type: 'fiat', keywords: ['brazil', 'real'] },
  'CAD': { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', type: 'fiat', keywords: ['canada', 'canadian', 'dollar'], minorUnit: 2 },
  'CHF': { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', type: 'fiat', keywords: ['switzerland', 'swiss', 'franc'], minorUnit: 2 },
  'CLP': { code: 'CLP', name: 'Chilean Peso', symbol: '$', type: 'fiat', keywords: ['chile', 'peso'] },
  'CNY': { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', type: 'fiat', keywords: ['china', 'yuan', 'renminbi'] },
  'COP': { code: 'COP', name: 'Colombian Peso', symbol: '$', type: 'fiat', keywords: ['colombia', 'peso'] },
  'CZK': { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč', type: 'fiat', keywords: ['czech', 'koruna'] },
  'DKK': { code: 'DKK', name: 'Danish Krone', symbol: 'kr', type: 'fiat', keywords: ['denmark', 'danish', 'krone'] },
  'EGP': { code: 'EGP', name: 'Egyptian Pound', symbol: '£', type: 'fiat', keywords: ['egypt', 'pound'] },
  'EUR': { code: 'EUR', name: 'Euro', symbol: '€', type: 'fiat', keywords: ['europe', 'european', 'euro'], minorUnit: 2 },
  'GBP': { code: 'GBP', name: 'British Pound', symbol: '£', type: 'fiat', keywords: ['britain', 'uk', 'pound', 'sterling'], minorUnit: 2 },
  'HKD': { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', type: 'fiat', keywords: ['hong kong', 'dollar'] },
  'HRK': { code: 'HRK', name: 'Croatian Kuna', symbol: 'kn', type: 'fiat', keywords: ['croatia', 'kuna'] },
  'HUF': { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft', type: 'fiat', keywords: ['hungary', 'forint'] },
  'IDR': { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', type: 'fiat', keywords: ['indonesia', 'rupiah'] },
  'ILS': { code: 'ILS', name: 'Israeli New Shekel', symbol: '₪', type: 'fiat', keywords: ['israel', 'shekel'] },
  'INR': { code: 'INR', name: 'Indian Rupee', symbol: '₹', type: 'fiat', keywords: ['india', 'rupee'] },
  'JPY': { code: 'JPY', name: 'Japanese Yen', symbol: '¥', type: 'fiat', keywords: ['japan', 'yen'], minorUnit: 0 },
  'KRW': { code: 'KRW', name: 'South Korean Won', symbol: '₩', type: 'fiat', keywords: ['korea', 'won'] },
  'KWD': { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك', type: 'fiat', keywords: ['kuwait', 'dinar'] },
  'MXN': { code: 'MXN', name: 'Mexican Peso', symbol: '$', type: 'fiat', keywords: ['mexico', 'peso'] },
  'MYR': { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', type: 'fiat', keywords: ['malaysia', 'ringgit'] },
  'NOK': { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', type: 'fiat', keywords: ['norway', 'krone'] },
  'NZD': { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', type: 'fiat', keywords: ['new zealand', 'dollar'] },
  'PEN': { code: 'PEN', name: 'Peruvian Sol', symbol: 'S/', type: 'fiat', keywords: ['peru', 'sol'] },
  'PHP': { code: 'PHP', name: 'Philippine Peso', symbol: '₱', type: 'fiat', keywords: ['philippines', 'peso'] },
  'PLN': { code: 'PLN', name: 'Polish Zloty', symbol: 'zł', type: 'fiat', keywords: ['poland', 'zloty'] },
  'QAR': { code: 'QAR', name: 'Qatari Riyal', symbol: '﷼', type: 'fiat', keywords: ['qatar', 'riyal'] },
  'RON': { code: 'RON', name: 'Romanian Leu', symbol: 'lei', type: 'fiat', keywords: ['romania', 'leu'] },
  'RSD': { code: 'RSD', name: 'Serbian Dinar', symbol: 'дин', type: 'fiat', keywords: ['serbia', 'dinar'] },
  'RUB': { code: 'RUB', name: 'Russian Ruble', symbol: '₽', type: 'fiat', keywords: ['russia', 'ruble'] },
  'SAR': { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼', type: 'fiat', keywords: ['saudi', 'riyal'] },
  'SEK': { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', type: 'fiat', keywords: ['sweden', 'krona'] },
  'SGD': { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', type: 'fiat', keywords: ['singapore', 'dollar'] },
  'THB': { code: 'THB', name: 'Thai Baht', symbol: '฿', type: 'fiat', keywords: ['thailand', 'baht'] },
  'TRY': { code: 'TRY', name: 'Turkish Lira', symbol: '₺', type: 'fiat', keywords: ['turkey', 'lira'] },
  'TWD': { code: 'TWD', name: 'Taiwan Dollar', symbol: 'NT$', type: 'fiat', keywords: ['taiwan', 'dollar'] },
  'UAH': { code: 'UAH', name: 'Ukrainian Hryvnia', symbol: '₴', type: 'fiat', keywords: ['ukraine', 'hryvnia'] },
  'USD': { code: 'USD', name: 'US Dollar', symbol: '$', type: 'fiat', keywords: ['united states', 'america', 'dollar'], minorUnit: 2 },
  'ZAR': { code: 'ZAR', name: 'South African Rand', symbol: 'R', type: 'fiat', keywords: ['south africa', 'rand'] },

  // Stablecoins (alphabetized)
  'BUSD': { code: 'BUSD', name: 'Binance USD', type: 'stablecoin', keywords: ['binance', 'stable', 'dollar'] },
  'DAI': { code: 'DAI', name: 'Dai Stablecoin', type: 'stablecoin', keywords: ['makerdao', 'dai', 'stable', 'decentralized'] },
  'FDUSD': { code: 'FDUSD', name: 'First Digital USD', type: 'stablecoin', keywords: ['first digital', 'stable', 'dollar'] },
  'FRAX': { code: 'FRAX', name: 'Frax', type: 'stablecoin', keywords: ['frax', 'algorithmic', 'stable'] },
  'GUSD': { code: 'GUSD', name: 'Gemini Dollar', type: 'stablecoin', keywords: ['gemini', 'stable', 'dollar'] },
  'LUSD': { code: 'LUSD', name: 'Liquity USD', type: 'stablecoin', keywords: ['liquity', 'stable', 'dollar'] },
  'PAXG': { code: 'PAXG', name: 'PAX Gold', type: 'stablecoin', keywords: ['pax', 'gold', 'backed'] },
  'PYUSD': { code: 'PYUSD', name: 'PayPal USD', type: 'stablecoin', keywords: ['paypal', 'stable', 'dollar'] },
  'TUSD': { code: 'TUSD', name: 'TrueUSD', type: 'stablecoin', keywords: ['true', 'stable', 'dollar'] },
  'USDC': { code: 'USDC', name: 'USD Coin', type: 'stablecoin', keywords: ['circle', 'coinbase', 'stable', 'dollar'] },
  'USDD': { code: 'USDD', name: 'USDD', type: 'stablecoin', keywords: ['tron', 'stable', 'dollar'] },
  'USDP': { code: 'USDP', name: 'Pax Dollar', type: 'stablecoin', keywords: ['pax', 'stable', 'dollar'] },
  'USDT': { code: 'USDT', name: 'Tether USD', type: 'stablecoin', keywords: ['tether', 'stable', 'dollar'] },
  'UST': { code: 'UST', name: 'TerraUSD', type: 'stablecoin', keywords: ['terra', 'luna', 'stable', 'algorithmic'] },

  // Major Cryptocurrencies (alphabetized)
  'ADA': { code: 'ADA', name: 'Cardano', type: 'cryptocurrency', keywords: ['cardano', 'ada', 'proof of stake'] },
  'ALGO': { code: 'ALGO', name: 'Algorand', type: 'cryptocurrency', keywords: ['algorand', 'algo'] },
  'ATOM': { code: 'ATOM', name: 'Cosmos', type: 'cryptocurrency', keywords: ['cosmos', 'atom', 'interchain'] },
  'AVAX': { code: 'AVAX', name: 'Avalanche', type: 'cryptocurrency', keywords: ['avalanche', 'avax'] },
  'BNB': { code: 'BNB', name: 'Binance Coin', type: 'cryptocurrency', keywords: ['binance', 'exchange'] },
  'BTC': { code: 'BTC', name: 'Bitcoin', symbol: '₿', type: 'cryptocurrency', keywords: ['bitcoin', 'digital gold', 'satoshi'] },
  'DOT': { code: 'DOT', name: 'Polkadot', type: 'cryptocurrency', keywords: ['polkadot', 'dot', 'parachain'] },
  'ETH': { code: 'ETH', name: 'Ethereum', symbol: 'Ξ', type: 'cryptocurrency', keywords: ['ethereum', 'ether', 'smart contracts'] },
  'FIL': { code: 'FIL', name: 'Filecoin', type: 'cryptocurrency', keywords: ['filecoin', 'fil', 'storage'] },
  'HBAR': { code: 'HBAR', name: 'Hedera', type: 'cryptocurrency', keywords: ['hedera', 'hbar', 'hashgraph'] },
  'ICP': { code: 'ICP', name: 'Internet Computer', type: 'cryptocurrency', keywords: ['internet computer', 'icp', 'dfinity'] },
  'LINK': { code: 'LINK', name: 'Chainlink', type: 'cryptocurrency', keywords: ['chainlink', 'link', 'oracle'] },
  'LTC': { code: 'LTC', name: 'Litecoin', type: 'cryptocurrency', keywords: ['litecoin', 'ltc', 'silver'] },
  'MATIC': { code: 'MATIC', name: 'Polygon', type: 'cryptocurrency', keywords: ['polygon', 'matic', 'ethereum', 'layer 2'] },
  'NEAR': { code: 'NEAR', name: 'NEAR Protocol', type: 'cryptocurrency', keywords: ['near', 'protocol', 'sharding'] },
  'SOL': { code: 'SOL', name: 'Solana', type: 'cryptocurrency', keywords: ['solana', 'sol', 'fast'] },
  'UNI': { code: 'UNI', name: 'Uniswap', type: 'cryptocurrency', keywords: ['uniswap', 'uni', 'dex', 'defi'] },
  'VET': { code: 'VET', name: 'VeChain', type: 'cryptocurrency', keywords: ['vechain', 'vet', 'supply chain'] },
  'XLM': { code: 'XLM', name: 'Stellar', type: 'cryptocurrency', keywords: ['stellar', 'xlm', 'lumens'] },
  'XRP': { code: 'XRP', name: 'Ripple', type: 'cryptocurrency', keywords: ['ripple', 'xrp', 'payments'] },
} as const satisfies Readonly<Record<string, CurrencyInfo>>;

/**
 * Currency codes array derived from database keys
 *
 * Alphabetically sorted (A→Z) for stable UI iteration.
 * Single source of truth - derived from CURRENCY_DATABASE to prevent drift.
 *
 * IMPORTANT: Do not maintain a separate explicit tuple. This array is
 * generated at module load time from the database keys to ensure consistency.
 */
export const CURRENCY_CODES = (Object.keys(CURRENCY_DATABASE) as (keyof typeof CURRENCY_DATABASE)[]).sort() as readonly (keyof typeof CURRENCY_DATABASE)[];

/**
 * Common currency pairs for forex trading - Major Pairs
 *
 * Major pairs are the 7 most liquid forex pairs, all involving USD as either
 * the base or quote currency. These pairs account for the majority of forex
 * trading volume globally.
 *
 * Format: 'BASE/QUOTE' using ISO 4217 3-letter currency codes
 * All pairs are curated explicit lists (not derived/generated)
 */
export const FOREX_MAJOR_PAIRS = [
  'EUR/USD', 'GBP/USD', 'USD/JPY', 'USD/CHF',
  'AUD/USD', 'USD/CAD', 'NZD/USD'
] as const;

/**
 * Common currency pairs for forex trading - Minor Pairs
 *
 * Minor pairs (also known as cross-currency pairs) are forex pairs that do not
 * include USD but involve major global currencies. These pairs have lower
 * liquidity than major pairs but are still actively traded.
 *
 * Format: 'BASE/QUOTE' using ISO 4217 3-letter currency codes
 * All pairs are curated explicit lists (not derived/generated)
 */
export const FOREX_MINOR_PAIRS = [
  'EUR/GBP', 'EUR/CHF', 'GBP/JPY', 'CHF/JPY',
  'EUR/JPY', 'GBP/CHF', 'AUD/JPY', 'CAD/JPY'
] as const;

/**
 * Compile-time smoke test ensuring database keys are valid string literals
 *
 * This minimal check verifies that CURRENCY_DATABASE keys are strings (as required
 * by the CurrencyCode type). Since CurrencyCode is now a nominal string type to avoid
 * circular dependencies, this check ensures basic type contract integrity.
 *
 * For precise compile-time validation of specific currency codes, consumers should
 * use `keyof typeof CURRENCY_DATABASE` directly from this constants module.
 */
type _CurrencyDatabaseKeys = keyof typeof CURRENCY_DATABASE extends string ? true : never;

/**
 * @internal
 * Compile-time coverage checks only - bundled export to eliminate unused variable warnings
 * while maintaining type safety. Do NOT re-export from barrels.
 */
export type _CurrencyTypeChecks = [
  _CurrencyDatabaseKeys
];

// Dev-only runtime validation guards
// These checks catch data integrity issues during development but are stripped from production builds
if (import.meta.env.MODE !== 'production') {
  const codes = Object.keys(CURRENCY_DATABASE);

  // Validation #1: Check all CURRENCY_DATABASE codes are unique
  const codeSet = new Set(codes);
  if (codeSet.size !== codes.length) {
    throw new Error(
      `[Currency Constants] CURRENCY_DATABASE contains duplicate currency codes. ` +
      `Expected ${codes.length} unique codes, found ${codeSet.size}.`
    );
  }

  // Validation #2: Check all currencies in FOREX_MAJOR_PAIRS exist in CURRENCY_DATABASE
  for (const pair of FOREX_MAJOR_PAIRS) {
    const parts = pair.split('/');
    const base = parts[0];
    const quote = parts[1];
    if (!base || !(base in CURRENCY_DATABASE)) {
      throw new Error(
        `[Currency Constants] FOREX_MAJOR_PAIRS contains unknown base currency: ${base} in pair ${pair}`
      );
    }
    if (!quote || !(quote in CURRENCY_DATABASE)) {
      throw new Error(
        `[Currency Constants] FOREX_MAJOR_PAIRS contains unknown quote currency: ${quote} in pair ${pair}`
      );
    }
  }

  // Validation #3: Check all currencies in FOREX_MINOR_PAIRS exist in CURRENCY_DATABASE
  for (const pair of FOREX_MINOR_PAIRS) {
    const parts = pair.split('/');
    const base = parts[0];
    const quote = parts[1];
    if (!base || !(base in CURRENCY_DATABASE)) {
      throw new Error(
        `[Currency Constants] FOREX_MINOR_PAIRS contains unknown base currency: ${base} in pair ${pair}`
      );
    }
    if (!quote || !(quote in CURRENCY_DATABASE)) {
      throw new Error(
        `[Currency Constants] FOREX_MINOR_PAIRS contains unknown quote currency: ${quote} in pair ${pair}`
      );
    }
  }

  // Validation #4a: Fiat minorUnit values (if present) are in approved set {0, 1, 2, 3}
  // ISO 4217 standard defines minorUnit as the number of decimal places for fiat currencies
  const fiatCurrenciesWithMinorUnit: Array<[string, number]> = [];
  for (const [code, info] of Object.entries(CURRENCY_DATABASE)) {
    if (info.type === 'fiat' && 'minorUnit' in info && info.minorUnit !== undefined) {
      fiatCurrenciesWithMinorUnit.push([code, info.minorUnit]);
    }
  }

  const invalidMinorUnits = fiatCurrenciesWithMinorUnit.filter(
    ([, minorUnit]) => ![0, 1, 2, 3].includes(minorUnit)
  );

  if (invalidMinorUnits.length > 0) {
    const invalidCodes = invalidMinorUnits.map(([code]) => code).join(', ');
    throw new Error(
      `[currency.ts] Invalid minorUnit values detected. ` +
      `Fiat currencies must have minorUnit in {0, 1, 2, 3} per ISO 4217. ` +
      `Invalid: ${invalidCodes}`
    );
  }

  // Validation #4b: Non-fiat currencies must NOT have minorUnit (fiat-only invariant)
  // Enforces type-level contract that minorUnit is exclusive to fiat currencies
  const nonFiatWithMinorUnit: Array<[string, string]> = [];
  for (const [code, info] of Object.entries(CURRENCY_DATABASE)) {
    if (info.type !== 'fiat' && 'minorUnit' in info && info.minorUnit !== undefined) {
      nonFiatWithMinorUnit.push([code, info.type]);
    }
  }

  if (nonFiatWithMinorUnit.length > 0) {
    const invalidCodes = nonFiatWithMinorUnit.map(([code, type]) =>
      `${code} (${type})`
    ).join(', ');
    throw new Error(
      `[currency.ts] minorUnit field is fiat-only per ISO 4217. ` +
      `Non-fiat currencies must not define minorUnit. ` +
      `Invalid: ${invalidCodes}`
    );
  }

  // Validation #5a: Check no duplicate pairs within FOREX_MAJOR_PAIRS
  const majorPairSet = new Set(FOREX_MAJOR_PAIRS);
  if (majorPairSet.size !== FOREX_MAJOR_PAIRS.length) {
    throw new Error(
      `[Currency Constants] FOREX_MAJOR_PAIRS contains duplicate pairs. ` +
      `Expected ${FOREX_MAJOR_PAIRS.length} unique pairs, found ${majorPairSet.size}.`
    );
  }

  // Validation #5b: Check no duplicate pairs within FOREX_MINOR_PAIRS
  const minorPairSet = new Set(FOREX_MINOR_PAIRS);
  if (minorPairSet.size !== FOREX_MINOR_PAIRS.length) {
    throw new Error(
      `[Currency Constants] FOREX_MINOR_PAIRS contains duplicate pairs. ` +
      `Expected ${FOREX_MINOR_PAIRS.length} unique pairs, found ${minorPairSet.size}.`
    );
  }

  // Validation #5c: Check no duplicate pairs across combined FOREX_MAJOR_PAIRS + FOREX_MINOR_PAIRS
  const combinedPairs = [...FOREX_MAJOR_PAIRS, ...FOREX_MINOR_PAIRS];
  const combinedPairSet = new Set(combinedPairs);
  if (combinedPairSet.size !== combinedPairs.length) {
    throw new Error(
      `[Currency Constants] Combined FOREX_MAJOR_PAIRS and FOREX_MINOR_PAIRS contain duplicate pairs. ` +
      `Expected ${combinedPairs.length} unique pairs across both arrays, found ${combinedPairSet.size}.`
    );
  }

  // Validation #6: Check all pairs match ISO 4217 3-letter format
  const pairFormatRegex = /^[A-Z]{3}\/[A-Z]{3}$/;
  for (const pair of [...FOREX_MAJOR_PAIRS, ...FOREX_MINOR_PAIRS]) {
    if (!pairFormatRegex.test(pair)) {
      throw new Error(
        `[Currency Constants] Forex pair does not match ISO 4217 3-letter format 'XXX/YYY': ${pair}`
      );
    }
  }

  // Validation #7: Check no self-pairs exist
  for (const pair of [...FOREX_MAJOR_PAIRS, ...FOREX_MINOR_PAIRS]) {
    const parts = pair.split('/');
    const base = parts[0];
    const quote = parts[1];
    if (base && quote && base === quote) {
      throw new Error(
        `[Currency Constants] Self-pair detected (base === quote): ${pair}`
      );
    }
  }

  // Validation #8: Check CURRENCY_CODES is alphabetically sorted
  const sortedCodes = CURRENCY_CODES.slice().sort();
  const codesMatch = CURRENCY_CODES.length === sortedCodes.length &&
    CURRENCY_CODES.every((code, index) => code === sortedCodes[index]);

  if (!codesMatch) {
    throw new Error(
      `[Currency Constants] CURRENCY_CODES is not alphabetically sorted. ` +
      `Expected first 5: [${sortedCodes.slice(0, 5).join(', ')}], ` +
      `found: [${CURRENCY_CODES.slice(0, 5).join(', ')}]`
    );
  }
}
